import {Artifact} from "../../../../../types";
import {readEmailPayload} from "../../artifacts/emailPayload";

export type EmailFenceResolution =
    | {status: "pending"}
    | {status: "invalid"}
    | {status: "ready"; artifact: Artifact};

/** Email selectors contain only an artifact id; content always comes from the registry. */
export function parseEmailFence(text: string): {artifactId: string} | null {
    try {
        const value: unknown = JSON.parse(text);
        if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
        const record = value as Record<string, unknown>;
        if (Object.keys(record).length !== 1 || typeof record.artifactId !== "string" || !record.artifactId.trim()) return null;
        return {artifactId: record.artifactId.trim()};
    } catch { return null; }
}

export function resolveEmailFence(text: string, artifacts: Artifact[] | undefined, isStreaming: boolean): EmailFenceResolution {
    const request = parseEmailFence(text);
    const artifact = request && artifacts?.find((candidate) => candidate.id === request.artifactId && candidate.type === "email_draft");
    if (artifact && readEmailPayload(artifact.payload)) return {status: "ready", artifact};
    return {status: isStreaming ? "pending" : "invalid"};
}

const EMAIL_FENCE = /^ {0,3}```email[ \t]*\r?\n([\s\S]*?)(?:\r?\n {0,3}```[ \t]*(?=\r?\n|$)|$)/gm;

export function resolvedInlineEmailIds(content: string, artifacts: Artifact[] | undefined): Set<string> {
    const ids = new Set<string>();
    for (const match of content.matchAll(EMAIL_FENCE)) {
        const resolution = resolveEmailFence(match[1], artifacts, false);
        if (resolution.status === "ready") ids.add(resolution.artifact.id);
    }
    return ids;
}

/** Repeated selectors share one view and one copy representation at their first position. */
export function deduplicateEmailFences(content: string): string {
    const ids = new Set<string>();
    return content.replace(EMAIL_FENCE, (fence, body: string) => {
        const request = parseEmailFence(body);
        if (!request) return fence;
        if (ids.has(request.artifactId)) return "";
        ids.add(request.artifactId);
        return fence;
    });
}
